import type { ColumnConfig } from '@/components/datatable/dataTableTypes';
import i18n from '@/plugins/i18n';

const { t } = i18n.global;
export interface CombinedColumnConfig {
  field: string;
  combinedField?: string[];
  template?: string;
}

export const processEnumFields = <T extends Record<string, any>>(
  data: T[],
  columns: ColumnConfig[] = [],
): T[] => {
  return data.map((item: T) => {
    columns.forEach((col: ColumnConfig) => {
      if (col.type === 'enum') {
        const enumValue = item[col.field];
        if (enumValue && !enumValue.startsWith('enums.')) {
          (item as Record<string, any>)[col.field] = processEnumField(enumValue);
        }
      }
    });
    return item;
  });
};

export const processEnumField = (enumValue: string): string => {
  return t(`enums.${enumValue.replace(/\s+/g, '_').toUpperCase()}`);
};

export const processCombinedFields = <T extends Record<string, any>>(
  data: T[],
  columns: CombinedColumnConfig[] = [],
): T[] => {
  const columnsToProcess = columns.filter((col) => col.combinedField);

  return data.map((item: T) => {
    columnsToProcess.forEach((col: CombinedColumnConfig) => {
      if (col.combinedField && col.template) {
        let filledTemplate = col.template;
        col.combinedField.forEach((field: string) => {
          filledTemplate = filledTemplate.replace(`\${${field}}`, `${item[field] ?? ''}`);
        });

        (item as Record<string, any>)[col.field] = filledTemplate;
      }
    });
    return item;
  });
};

export const prepareTextData = <T extends Record<string, any>>(
  data: T[],
  columns: ColumnConfig[] = [],
  truncateLength: number = 50,
): T[] => {
  return data.map((item: T) => {
    columns.forEach((col: ColumnConfig) => {
      if (typeof item[col.field] === 'string' && !col.combinedField) {
        const fullText = item[col.field] as string;

        if (fullText.length <= (col.truncateLength || truncateLength)) return;

        const truncatedText =
          fullText.length > (col.truncateLength || truncateLength)
            ? fullText.slice(0, col.truncateLength || truncateLength) + '...'
            : fullText;

        (item as Record<string, any>)[`_truncated_${col.field}`] = truncatedText;
      }
    });
    return item;
  });
};
